import { FC, c } from 'lib/component-utils'
import Container from 'components/landing/Container'
import { ProgressiveBlur } from 'components/landing/Header'
import { LazyMotion, domAnimation, m, useReducedMotion } from 'motion/react'
import sellmonitorFeatures from 'components/landing/lib/sellmonitorFeatures'
import { match } from 'ts-pattern'
import { useEffect, useRef, useState } from 'react'
import Button from 'components/base/Button'
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon } from '@heroicons/react/solid'
import Link from 'components/base/Link'

type Props = {
  mall: 'wb' | 'ozon' | 'beru'
  showMall?: boolean
  alignCenter?: boolean
  features?: typeof sellmonitorFeatures
  heading?: string
  buttonTitle?: string
  language?: 'ru' | 'en' | 'zh' | 'uz'
}

const SellmonitorFeatures: FC<Props> = ({
  className,
  id,
  mall = 'wb',
  showMall = true,
  alignCenter = false,
  features = sellmonitorFeatures,
  heading = 'Возможности Sellmonitor',
  buttonTitle = 'Узнать больше',
  language = 'ru',
}) => {
  const navigation = {
    ru: {
      sections: 'Разделы возможностей',
      previous: 'Предыдущий раздел',
      next: 'Следующий раздел',
      of: 'из',
    },
    en: {
      sections: 'Feature sections',
      previous: 'Previous section',
      next: 'Next section',
      of: 'of',
    },
    zh: { sections: '功能分类', previous: '上一项功能', next: '下一项功能', of: '/' },
    uz: {
      sections: 'Imkoniyatlar bo‘limlari',
      previous: 'Oldingi bo‘lim',
      next: 'Keyingi bo‘lim',
      of: '/',
    },
  }[language]
  const [selectedFeature, setSelectedFeature] = useState(0)
  const navigationRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const touchRef = useRef<{ x: number; y: number; cancelled: boolean } | null>(null)
  const swipedRef = useRef(0)
  const [isMobile, setIsMobile] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [slideHeight, setSlideHeight] = useState<number>()
  const reduceMotion = useReducedMotion()
  const filteredFeatures = features.filter(({ malls }) => malls.includes(mall))
  const mallName = match(mall)
    .with('wb', () => 'Wildberries')
    .with('ozon', () => 'Ozon')
    .with('beru', () => 'Яндекс Маркете')
    .exhaustive()
  const imageAlt = (text: string) => (showMall ? `${text} на ${mallName}` : text)

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1023px)')
    const updateMobile = () => setIsMobile(media.matches)
    updateMobile()
    media.addEventListener('change', updateMobile)
    const header = document.querySelector('header')
    const updateHeader = () => {
      setHeaderHeight(
        header && ['fixed', 'sticky'].includes(getComputedStyle(header).position)
          ? header.offsetHeight
          : 0
      )
    }
    const resizeObserver = new ResizeObserver(updateHeader)
    const mutationObserver = new MutationObserver(updateHeader)
    if (header) {
      resizeObserver.observe(header)
      mutationObserver.observe(header, { attributes: true, attributeFilter: ['class'] })
    }
    updateHeader()
    return () => {
      media.removeEventListener('change', updateMobile)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [])

  useEffect(() => setSelectedFeature(0), [mall])

  useEffect(() => {
    if (!isMobile) return
    const tab = tabsRef.current?.children[selectedFeature] as HTMLElement | undefined
    if (tab && tabsRef.current) {
      tabsRef.current.scrollTo({
        left: tab.offsetLeft - (tabsRef.current.clientWidth - tab.offsetWidth) / 2,
        behavior: 'instant',
      })
    }
    const observer = new ResizeObserver(([entry]) => {
      setSlideHeight(entry.target.getBoundingClientRect().height)
    })
    const slide = navigationRef.current?.nextElementSibling?.querySelector('li[aria-hidden="false"]')
    if (slide) observer.observe(slide)
    return () => observer.disconnect()
  }, [selectedFeature, mall, isMobile, reduceMotion])

  const selectFeature = (index: number) => {
    if (index < 0 || index >= filteredFeatures.length || index === selectedFeature) return
    if (isMobile && navigationRef.current) {
      const top =
        (navigationRef.current.previousElementSibling?.getBoundingClientRect().bottom ?? 0) + 16
      if (top < headerHeight) {
        window.scrollTo({ top: window.scrollY + top - headerHeight, behavior: 'instant' })
      }
    }
    setSelectedFeature(index)
  }

  return (
    <Container id={id} className={c`!bg-gray-100 !pt-10 !pb-10 lg:!pt-20 lg:!pb-24 ${className}`}>
      <h2
        className={c`text-3xl font-medium
          ${alignCenter} text-center | text-left
        `}
      >
        {heading}
        {showMall &&
          match(mall)
            .with('wb', () => ' для Wildberries')
            .with('ozon', () => ' для Ozon')
            .with('beru', () => ' для Яндекс Маркета')
            .run()}
      </h2>
      <div
        ref={navigationRef}
        style={{ top: isMobile ? headerHeight : undefined }}
        className="sticky z-30 -mx-4 mt-4 px-4 pt-3 pb-1 sm:-mx-6 sm:px-6 lg:relative lg:mx-0 lg:mt-8 lg:bg-gray-100 lg:p-0"
      >
        <div className="lg:hidden">
          <ProgressiveBlur onDark={false} />
        </div>
        <div
          ref={tabsRef}
          role="group"
          aria-label={navigation.sections}
          className={c`flex flex-nowrap lg:flex-wrap overflow-x-auto gap-2 max-w-full font-medium lg:gap-4 w-fit relative
            ${alignCenter} lg:justify-center | lg:justify-start
          `}
        >
          {filteredFeatures.map(({ title }, index) => (
            <button
              key={title}
              aria-pressed={selectedFeature === index}
              onClick={() => selectFeature(index)}
              className={c`
                    px-4 py-2 lg:px-6 lg:py-3 rounded-full border transition-all duration-300 ease-in-out
                    flex items-center gap-1 sm:gap-3 text-base flex-nowrap text-nowrap
                    ${
                      selectedFeature !== index
                        ? 'bg-white border-gray-200 text-gray-900 hover:bg-primary-100 hover:border-primary-200'
                        : 'bg-primary-600 border-primary-600 text-white'
                    }
                  `}
            >
              {title}
            </button>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-center gap-4 lg:hidden">
          <Button
            theme="gray"
            aria-label={navigation.previous}
            disabled={selectedFeature === 0}
            className="!rounded-full !p-3"
            onClick={() => selectFeature(selectedFeature - 1)}
          >
            <ChevronLeftIcon className="size-5" aria-hidden="true" />
          </Button>
          <span className="min-w-16 text-center text-sm text-gray-600" aria-live="polite">
            {selectedFeature + 1} {navigation.of} {filteredFeatures.length}
          </span>
          <Button
            theme="gray"
            aria-label={navigation.next}
            disabled={selectedFeature === filteredFeatures.length - 1}
            className="!rounded-full !p-3"
            onClick={() => selectFeature(selectedFeature + 1)}
          >
            <ChevronRightIcon className="size-5" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div className="w-full relative mt-2 lg:mt-12">
        <LazyMotion features={domAnimation}>
          <m.ul
            animate={{ height: isMobile && slideHeight !== undefined ? slideHeight : 'auto' }}
            transition={{ duration: reduceMotion ? 0 : 0.2 }}
            className={c`w-full relative touch-pan-y overflow-hidden md:grid md:!h-auto lg:overflow-visible ${Boolean(filteredFeatures[selectedFeature]?.mainImage)} md:min-h-[428px] lg:min-h-[495px] xl:min-h-[612px]`}
            onTouchStart={(event) => {
              swipedRef.current = 0
              touchRef.current =
                isMobile && event.touches.length === 1
                  ? { x: event.touches[0].clientX, y: event.touches[0].clientY, cancelled: false }
                  : null
            }}
            onTouchMove={(event) => {
              if (!touchRef.current) return
              if (
                event.touches.length !== 1 ||
                Math.abs(event.touches[0].clientY - touchRef.current.y) >
                  Math.max(12, Math.abs(event.touches[0].clientX - touchRef.current.x))
              ) {
                touchRef.current.cancelled = true
              }
            }}
            onTouchCancel={() => {
              touchRef.current = null
            }}
            onTouchEnd={(event) => {
              if (!touchRef.current || touchRef.current.cancelled) {
                touchRef.current = null
                return
              }
              const distance = event.changedTouches[0].clientX - touchRef.current.x
              if (
                Math.abs(distance) > 50 &&
                Math.abs(event.changedTouches[0].clientY - touchRef.current.y) < Math.abs(distance)
              ) {
                swipedRef.current = Date.now()
                selectFeature(selectedFeature + (distance < 0 ? 1 : -1))
              }
              touchRef.current = null
            }}
            onClickCapture={(event) => {
              if (swipedRef.current && Date.now() - swipedRef.current < 500) {
                event.preventDefault()
                event.stopPropagation()
                swipedRef.current = 0
              }
            }}
          >
            {filteredFeatures.map(
              (
                {
                  title,
                  items,
                  benefit,
                  mainImage,
                  className: mainClassName,
                  gridClassName,
                  link,
                },
                index
              ) => selectedFeature === index || (mainImage && filteredFeatures[selectedFeature]?.mainImage) ? (
                <m.li
                  key={title}
                  aria-hidden={selectedFeature !== index}
                  initial={{ opacity: reduceMotion ? 1 : 0, x: reduceMotion ? 0 : 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: reduceMotion ? 0 : 0.2 }}
                  className={c`w-full relative md:col-start-1 md:row-start-1 md:self-start ${selectedFeature !== index} hidden md:block invisible pointer-events-none`}
                >
                  <div className="w-full h-auto flex flex-col relative z-10 items-center justify-center">
                    <div className="text-xl font-medium text-center w-full mb-4">{benefit}</div>
                    <div className="w-full h-fit relative">
                      {mainImage && (
                        <div className="block p-2 w-full lg:w-3/4 mx-auto rounded-xl ring-1 ring-inset bg-gray-900/5 ring-gray-900/10 lg:rounded-2xl lg:p-4">
                          <img
                            src={mainImage.src}
                            alt={imageAlt(benefit)}
                            width={mainImage.width}
                            height={mainImage.height}
                            className="w-full h-auto rounded-md ring-1 ring-gray-900/10"
                            loading={selectedFeature === index ? 'eager' : 'lazy'}
                          />
                        </div>
                      )}
                      <div
                        className={c`w-full mt-4 lg:mt-0 gap-4 lg:gap-0 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:block ${mainClassName} ${gridClassName?.[mall]}`}
                      >
                        {items
                          .filter(({ malls: itemMalls }) => itemMalls?.includes(mall) ?? true)
                          .map(({ description, image, className, link: itemLink }) => {
                            const content = (
                              <>
                                {image && (
                                  <img
                                    src={image.src}
                                    alt={imageAlt(description)}
                                    width={image.width}
                                    height={image.height}
                                    className="w-full h-auto rounded-xl lg:min-h-[91px]"
                                    loading="lazy"
                                  />
                                )}
                                <div className="w-full p-2 text-center">
                                  <span className="text-base">{description}</span>
                                </div>
                              </>
                            )

                            const itemClassName = c`bg-white p-2 col-span-1 lg:h-fit static lg:absolute z-20 rounded-2xl lg:shadow-2xl lg:max-w-80 ring-1 flex flex-col items-center justify-center gap-2 overflow-hidden ${className} ${itemLink ? 'cursor-pointer ring-gray-900/5 hover:ring-primary-500 transition-colors' : 'ring-gray-900/5'}`

                            return itemLink ? (
                              <Link
                                key={description}
                                href={itemLink}
                                className={itemClassName}
                                innerClassName="flex w-full flex-col items-center justify-center gap-2"
                                target="_blank"
                              >
                                {content}
                              </Link>
                            ) : (
                              <div key={description} className={itemClassName}>
                                {content}
                              </div>
                            )
                          })}
                      </div>
                    </div>
                    {link && (
                      <Button
                        as={Link}
                        href={link}
                        target="_blank"
                        theme="secondary"
                        className="mt-4 lg:mt-20"
                        innerClassName="inline-flex items-center gap-2"
                      >
                        <span>{buttonTitle}</span>
                        <ArrowRightIcon className="size-4 shrink-0" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </m.li>
              ) : null
            )}
          </m.ul>
        </LazyMotion>
        <div className="w-full hidden lg:flex flex-row items-center justify-center mt-20 gap-8">
          <Button
            theme="gray"
            aria-label={navigation.previous}
            className="!rounded-full !px-2 disabled:hover:!bg-transparent disabled:hover:!text-primary-600 !ring-0 !ring-offset-0 !ring-transparent"
            disabled={selectedFeature === 0}
            onClick={() => {
              if (selectedFeature === 0) return
              selectFeature(selectedFeature - 1)
            }}
          >
            <ChevronLeftIcon className="size-6 shrink-0" />
          </Button>
          <ol className="flex-row items-center justify-center hidden sm:flex">
            {filteredFeatures.map((_, index) => (
              <li key={index}>
                <button
                  key={`dot-${index}`}
                  aria-label={filteredFeatures[index].title}
                  aria-pressed={selectedFeature === index}
                  onClick={() => selectFeature(index)}
                  className="px-2 py-2 flex items-center group"
                >
                  <span
                    className={c`size-2 rounded-full ${
                      selectedFeature !== index
                        ? 'bg-gray-300 group-hover:bg-gray-400'
                        : 'bg-gray-500'
                    }`}
                  />
                </button>
              </li>
            ))}
          </ol>
          <Button
            theme="gray"
            aria-label={navigation.next}
            className="!rounded-full !px-2 disabled:hover:!bg-transparent disabled:hover:!text-primary-600 !ring-0 !ring-offset-0 !ring-transparent"
            disabled={selectedFeature === filteredFeatures.length - 1}
            onClick={() => {
              if (selectedFeature === filteredFeatures.length - 1) return
              selectFeature(selectedFeature + 1)
            }}
          >
            <ChevronRightIcon className="size-6 shrink-0" />
          </Button>
        </div>
      </div>
    </Container>
  )
}

export default SellmonitorFeatures
